/**
 * ENG-6157 (Phase 2) — live read-only tool call through the agent's wired
 * Composio MCP server.
 *
 * Phase 1 (`assessAuthConfigLinkage`) deterministically catches the
 * auth_config/server mismatch from metadata. This is the belt-and-suspenders
 * leg the issue's fix-option-1 describes: actually exercise the agent's *wired
 * MCP URL* with a real tool call — the ONLY call that proves the server
 * resolves the connected account end-to-end. `tools/list` is green even when no
 * account resolves; only `tools/call` surfaces `No connected account found …`.
 *
 * Safety: we never hardcode tool slugs (they drift per toolkit/version). Instead
 * we read the server's own `tools/list` and pick a tool that is provably
 * side-effect-free: its name carries a read-only verb (LIST/GET/SEARCH/…) AND
 * its input schema has no required parameters, so calling it with `{}` cannot
 * mutate anything. If no such tool exists we return `null` (skip) — Phase 1
 * still governs. A wrong guess therefore degrades to a no-op, never a false
 * result and never a side effect.
 *
 * Outcome:
 *   - `null`             — no safe tool to call (skip; not a verdict)
 *   - `'ok'`             — the call resolved the connected account (success, or
 *                          a benign non-account error like arg validation). When
 *                          it was the latter the outcome is QUALIFIED and
 *                          carries `details.tool_error` (ENG-8772) — callers must
 *                          not render a qualified ok as a plain success.
 *   - `'down'`           — either the wired server couldn't resolve the account
 *                          (account-resolution error), OR (ENG-6328) it resolved
 *                          the account but the upstream provider rejected its
 *                          credential (401/403 / "authentication required" /
 *                          `successful:false` envelope) → reconnect required
 *   - `'degraded'`       — the provider was REACHED and the credential ACCEPTED,
 *                          but the call was refused for a reason a reconnect
 *                          cannot fix. `details.reason` says which:
 *                          `scope_deficit` (ENG-8406 — grant the scope in the
 *                          provider app) or `quota_exhausted` (ENG-8772 — wait
 *                          for the window, `details.retry_after_seconds` when the
 *                          provider gave one). Never green, never a status
 *                          downgrade: the credential is proven good, so writing
 *                          status 'error' would de-provision the agent's tools.
 *   - `'transient_error'`— transport/timeout/5xx (retryable)
 *
 * `fetch` is injected for unit-testing. Streamable-HTTP MCP only (Composio).
 */

import type { ConnectivityProbeOutcome } from './connectivity-probe.js';

const MCP_ACCEPT = 'application/json, text/event-stream';
const DEFAULT_TIMEOUT_MS = 10_000;

/** Verb tokens that mark a Composio tool as read-only (no mutation). */
export const READONLY_VERB_TOKENS = [
  'LIST',
  'GET',
  'FIND',
  'SEARCH',
  'FETCH',
  'COUNT',
  'RETRIEVE',
  'READ',
] as const;

/** Composio error fragments that mean "the wired server couldn't resolve the account". */
const ACCOUNT_RESOLUTION_ERROR_PATTERNS = [
  'no connected account',
  'connected account not found',
  'no account found',
  'could not be resolved',
  'auth config',
  'no connection found',
] as const;

/**
 * ENG-6328 — phrases that mean the wired server RESOLVED the connected account
 * but the UPSTREAM provider rejected its credential (a dead/revoked OAuth grant,
 * an expired token, a 401/403). This is distinct from an account-resolution
 * failure (the Composio-side miss above) and from a benign tool error (bad
 * args): it's the "integration is broken, reconnect required" signal that
 * Composio's own record hides — it keeps the account ACTIVE with
 * `auth_refresh_required:false`, so only a live call surfaces it (the sherlock
 * incident: every Linear call 401'd while the console Test stayed green).
 */
const UPSTREAM_AUTH_ERROR_PATTERNS = [
  'authentication required',
  'not authenticated',
  'unauthorized',
  'authentication_error',
  'authentication error',
  'invalid authentication',
  'invalid credentials',
  'invalid api key',
  'invalid access token',
  'token expired',
  'token has expired',
  'expired access token',
  'expired credentials',
  'permission denied',
  'access denied',
  'forbidden',
] as const;

/**
 * ENG-7732 - phrases that mean the provider request never reached a real host
 * because the derived site/base URL was empty or malformed, so the bare scheme
 * (or nothing) got treated as a hostname. The canonical symptom for a Jira
 * connection with no resolvable Atlassian site is undici's
 * "DNS resolution failed for 'https'" (getaddrinfo ENOTFOUND https). This is
 * distinct from an account-resolution miss and from an upstream 401/403: the
 * account resolved and the credential may be fine, but there is no site to call.
 * Without this bucket the failure fell through to 'benign' and wrongly read as a
 * passing "account resolved" verdict, so a Jira connect reported "connected".
 */
const SITE_RESOLUTION_ERROR_PATTERNS = [
  'dns resolution failed',
  'getaddrinfo',
  'enotfound',
  'could not resolve host',
  'name resolution',
  'name not resolved',
] as const;

/**
 * ENG-8406 — phrases that mean the account resolved and the credential is VALID,
 * but the provider refused the call because the connection's app/token is not
 * GRANTED a required permission scope. This is distinct from a dead credential
 * (`isUpstreamAuthError`): reconnecting the SAME app does NOT help — the operator
 * must grant the missing scope in the provider app and re-authorise. The
 * canonical case is a Shopify custom app: "Access forbidden … the API token
 * doesn't have the required 'read_content' or 'write_content' scope, or the
 * Online Store sales channel is not enabled." Such a 403 ALSO matches the auth
 * 'forbidden' pattern, so `classifyToolCallFailure` must check scope FIRST.
 */
const SCOPE_DEFICIT_ERROR_PATTERNS = [
  'insufficient_scope',
  'insufficient scope',
  'insufficient permission',
  'missing scope',
  'required scope',
  'requires the scope',
  'requires the following scope',
  'scope is required',
  'access scope',
  'oauth scope',
  // Shopify custom-app specifics.
  'read_content',
  'write_content',
  'sales channel is not enabled',
] as const;

/**
 * ENG-8772 — phrases that mean the account resolved, the credential is VALID and
 * was ACCEPTED, but the provider refused the call because a quota or rate limit
 * is exhausted. This is distinct from every other bucket: nothing is broken and
 * nothing needs reconnecting, yet the integration is genuinely unusable until
 * the window resets, so it must NOT read as a pass.
 *
 * Without this bucket a 429 fell through to `benign`, whose reasoning ("a bad
 * argument still proves the credential works") is sound for a validation error
 * and wrong here: a quota error proves the credential works AND that every agent
 * call will fail until the window resets. The canonical payload is a Google Ads
 * `RESOURCE_EXHAUSTED`: "Resource has been exhausted (e.g. check quota). …
 * Too many requests. Retry in 41320 seconds."
 *
 * Checked BEFORE `auth` in `classifyToolCallFailure`: providers commonly return
 * quota refusals as a 403, which would otherwise match the auth 'forbidden'
 * pattern and be misreported as a dead credential requiring a reconnect.
 */
const QUOTA_EXHAUSTED_ERROR_PATTERNS = [
  'resource_exhausted',
  'resource has been exhausted',
  'rate limit',
  'rate_limit',
  'ratelimit',
  'ratescope',
  'rate_scope',
  'too many requests',
  'quota exceeded',
  'quota_exceeded',
  'quotaexceeded',
  'quota error',
  'quotaerror',
  'quota_error',
  'exceeded your quota',
  'out of quota',
  'insufficient quota',
  'check quota',
  'usage limit',
  'daily limit exceeded',
] as const;

export interface McpToolDescriptor {
  name: string;
  inputSchema?: { required?: string[] } | null;
}

/**
 * True when a tool descriptor is provably side-effect-free: its name carries a
 * read-only verb token AND it declares no required input parameters, so calling
 * it with `{}` cannot mutate. This is the STRUCTURAL read-only guard — both the
 * heuristic pick and the operator-stored override (ENG-6212) must pass it, so a
 * stored slug can never escape the invariant (it only chooses *which* safe tool,
 * never whether the call is safe).
 */
export function isReadonlyToolDescriptor(t: McpToolDescriptor | undefined | null): boolean {
  if (!t?.name) return false;
  // Tokenized match (not substring): the read verb must be a STANDALONE token,
  // so a mutating name like TARGET_UPDATE (which contains "GET" as a substring)
  // is not misclassified as read-only (CodeRabbit PR #1963). Composio slugs are
  // underscore-delimited (e.g. GMAIL_GET_PROFILE → [GMAIL, GET, PROFILE]).
  const tokens = t.name.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  const hasReadVerb = tokens.some((tok) => (READONLY_VERB_TOKENS as readonly string[]).includes(tok));
  if (!hasReadVerb) return false;
  const required = t.inputSchema?.required ?? [];
  return !(Array.isArray(required) && required.length > 0);
}

/**
 * Pick a provably side-effect-free tool from a server's tool list: name carries
 * a read-only verb token AND no required input parameters. Returns the tool
 * name, or `null` when none qualifies.
 */
export function pickSafeReadonlyTool(tools: McpToolDescriptor[]): string | null {
  for (const t of tools) {
    if (isReadonlyToolDescriptor(t)) return t.name;
  }
  return null;
}

/** Why an operator-stored override tool was not used (ENG-6212). */
export type OverrideFallbackReason =
  /** Stored slug is not in the server's live tools/list (the slug drifted). */
  | 'seed-drift'
  /** Stored slug exists but isn't structurally read-only (a bad seed; CI should have caught it). */
  | 'seed-invalid';

export interface ResolvedProbeTool {
  /** The tool that will actually be called (override when valid, else heuristic pick, else null). */
  toolName: string | null;
  /** Args to call it with — the stored args only when the override itself is used, else `{}`. */
  args: Record<string, unknown>;
  /** Set when a stored override was requested but NOT used (caller logs reason=<this>). */
  fallback?: OverrideFallbackReason;
  /** The override slug that was requested, when one was. */
  requestedTool?: string;
}

/**
 * Resolve which tool the probe should call, given the live tool list and an
 * optional operator-stored override (ENG-6212). The override is honoured ONLY
 * when it is present in the live list AND structurally read-only; otherwise we
 * fall back to the heuristic pick and report why (`seed-drift` / `seed-invalid`)
 * so the caller can emit a distinct log line. Never runs a non-read-only tool.
 */
export function resolveProbeTool(
  tools: McpToolDescriptor[],
  override?: { tool?: string | null; args?: Record<string, unknown> | null },
): ResolvedProbeTool {
  const requested = override?.tool?.trim();
  if (!requested) {
    return { toolName: pickSafeReadonlyTool(tools), args: {} };
  }
  const match = tools.find((t) => t?.name === requested);
  if (!match) {
    return { toolName: pickSafeReadonlyTool(tools), args: {}, fallback: 'seed-drift', requestedTool: requested };
  }
  if (!isReadonlyToolDescriptor(match)) {
    return { toolName: pickSafeReadonlyTool(tools), args: {}, fallback: 'seed-invalid', requestedTool: requested };
  }
  return { toolName: requested, args: override?.args ?? {}, requestedTool: requested };
}

/** True when a tool-call error message indicates the account couldn't be resolved. */
export function isAccountResolutionError(message: string): boolean {
  const m = message.toLowerCase();
  return ACCOUNT_RESOLUTION_ERROR_PATTERNS.some((p) => m.includes(p));
}

/**
 * ENG-6328 — True when a tool-call failure indicates the upstream provider
 * rejected the connection's credential (a revoked/expired OAuth grant): an
 * explicit auth phrase, OR a structured 401/403 surfaced in Composio's failure
 * envelope (`statusCode` / `status_code` / `http.status` /
 * `mercury_last_http_status_code` / "401 Client Error"). Tolerant of JSON
 * escaping (`\"statusCode\":401`). Callers MUST only consult this on a failure
 * (a JSON-RPC error, `result.isError`, or a `successful:false` envelope) — a bare
 * "401"/"forbidden" inside a SUCCESSFUL read's data must never flip the verdict.
 */
export function isUpstreamAuthError(message: string): boolean {
  const m = message.toLowerCase();
  if (UPSTREAM_AUTH_ERROR_PATTERNS.some((p) => m.includes(p))) return true;
  if (/\b(401|403)\s+client error/.test(m)) return true;
  // A 4xx-auth status adjacent to a status field (escaping/quoting tolerant).
  return /(status(?:_?code)?|http_?status(?:_code)?|mercury_last_http_status_code)["'\\\s]*[:=]["'\\\s]*(401|403)\b/.test(m);
}

/**
 * ENG-6328 — True when a Composio tool RESULT payload reports its OWN failure
 * (`successful:false`), even though the MCP `tools/call` itself returned 200 with
 * no `isError` flag. Composio wraps an upstream provider error this way, so
 * without this check a 401-bearing result falls through to a green "resolved the
 * account". Tolerant of Composio's `successful`/`successfull` (sic) spellings and
 * of JSON escaping. Only an EXPLICIT `false` counts.
 */
export function isComposioFailureEnvelope(text: string): boolean {
  return /["'\\]*(successful|successfull)["'\\\s]*:\s*false\b/i.test(text);
}

/**
 * ENG-6328 — classify a tool-call FAILURE into the three verdict-bearing kinds.
 * Account-resolution is checked first (it's the Composio-side miss the probe was
 * built for); upstream-auth next (the dead-credential signal); everything else
 * is a benign tool error (bad args, validation) that still proves the account
 * resolved AND the credential was accepted.
 */
/**
 * ENG-7732 - True when a tool-call failure indicates the provider request never
 * reached a real host because the derived site/base URL was empty or malformed
 * (e.g. Jira with no accessible Atlassian site: "DNS resolution failed for
 * 'https'"). Callers MUST only consult this on a failure - a hostname string in
 * a SUCCESSFUL read's data must never flip the verdict.
 */
export function isSiteResolutionError(message: string): boolean {
  const m = message.toLowerCase();
  return SITE_RESOLUTION_ERROR_PATTERNS.some((p) => m.includes(p));
}

/**
 * ENG-8406 — True when a tool-call failure is a MISSING-SCOPE deficit (the
 * credential is VALID but the app/token isn't granted a required permission),
 * NOT a dead credential. Either an explicit scope-deficit phrase, or the generic
 * "…doesn't have the required … scope" shape (a scope mention paired with an
 * absence/requirement word), which avoids hard-coding every provider's scope
 * names. Callers MUST only consult this on a failure.
 */
export function isScopeDeficitError(message: string): boolean {
  const m = message.toLowerCase();
  if (SCOPE_DEFICIT_ERROR_PATTERNS.some((p) => m.includes(p))) return true;
  return (
    m.includes('scope') &&
    ["doesn't have", 'does not have', 'not have the', 'not granted', 'lacks the', 'not authorized for'].some((p) =>
      m.includes(p),
    )
  );
}

/**
 * ENG-8772 — True when a tool-call failure is a QUOTA / RATE-LIMIT refusal: the
 * account resolved and the credential was accepted, but the provider will refuse
 * every call until the window resets. Either an explicit quota phrase, or a
 * structured 429 in the failure envelope (`statusCode` / `status_code` /
 * `http.status` / `code` / `mercury_last_http_status_code`), tolerant of JSON
 * escaping. Callers MUST only consult this on a failure — a "rate limit" string
 * inside a SUCCESSFUL read's data (e.g. listing a provider's own rate-limit
 * settings) must never flip the verdict.
 */
export function isQuotaExhaustedError(message: string): boolean {
  const m = message.toLowerCase();
  if (QUOTA_EXHAUSTED_ERROR_PATTERNS.some((p) => m.includes(p))) return true;
  if (/\b429\s+client error/.test(m)) return true;
  // A 429 adjacent to a status/code field (escaping/quoting tolerant). `code` is
  // included because Google's error envelope nests the status there
  // (`{"error":{"code":429,…}}`) rather than in an HTTP-named field.
  return /(status(?:_?code)?|http_?status(?:_code)?|mercury_last_http_status_code|["'\\]code)["'\\\s]*[:=]["'\\\s]*429\b/.test(
    m,
  );
}

/**
 * ENG-8772 — pull the provider's own retry window out of a quota failure, in
 * seconds. "Retry in 41320 seconds" is the single most useful fact in a Google
 * `RESOURCE_EXHAUSTED` payload and was previously buried in raw JSON. Also
 * handles the `Retry-After` header shape when a provider echoes it into the
 * body. Returns `null` when the provider gave no window — we never invent one.
 */
export function extractRetryAfterSeconds(message: string): number | null {
  const m = message.toLowerCase();
  const patterns = [
    /retry\s+in\s+(\d+)\s*(?:s\b|sec\b|secs\b|second)/,
    /retry[-_ ]?after["'\\\s]*[:=]["'\\\s]*(\d+)/,
    /retry_?delay["'\\\s]*[:=]["'\\\s]*"?(\d+)s?/,
  ];
  for (const re of patterns) {
    const hit = re.exec(m);
    if (!hit?.[1]) continue;
    const seconds = Number(hit[1]);
    if (Number.isFinite(seconds) && seconds > 0) return seconds;
  }
  return null;
}

/**
 * ENG-8772 — render a retry window as something an operator can act on. 41320
 * seconds is a number nobody can read; "about 11.5 hours" is a decision.
 */
export function formatRetryWindow(seconds: number): string {
  if (seconds < 90) return `${seconds} seconds`;
  const minutes = seconds / 60;
  if (minutes < 90) return `about ${Math.round(minutes)} minutes`;
  const hours = minutes / 60;
  // One decimal place up to a day — "about 11.5 hours" is more useful than
  // "about 12 hours" when the operator is deciding whether to wait.
  if (hours < 24) return `about ${Math.round(hours * 10) / 10} hours`;
  return `about ${Math.round((hours / 24) * 10) / 10} days`;
}

export function classifyToolCallFailure(
  text: string,
): 'account' | 'quota' | 'scope' | 'auth' | 'site' | 'benign' {
  if (isAccountResolutionError(text)) return 'account';
  // ENG-8772: check quota BEFORE scope and auth. Providers commonly return a
  // quota refusal as a 403, which matches the auth 'forbidden' pattern — and
  // "you have exceeded your quota for this scope" would match the scope bucket.
  // Both would send the operator to reconnect or re-grant, neither of which is
  // the problem: the credential is fine and the fix is to wait.
  if (isQuotaExhaustedError(text)) return 'quota';
  // ENG-8406: a MISSING-SCOPE 403 also matches the auth 'forbidden' pattern, so
  // check scope FIRST — its action is "grant the scope in the app", NOT a
  // reconnect (reconnecting the same app changes nothing).
  if (isScopeDeficitError(text)) return 'scope';
  if (isUpstreamAuthError(text)) return 'auth';
  // ENG-7732: a DNS/host-resolution failure means the derived site URL is
  // unusable - surface it distinctly rather than as a benign (passing) verdict.
  if (isSiteResolutionError(text)) return 'site';
  return 'benign';
}

/** Extract the JSON-RPC response for `expectedId` from a JSON or SSE body. */
async function parseRpc(res: Response, expectedId: number): Promise<Record<string, unknown> | null> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('text/event-stream')) {
    const text = await res.text();
    let dataLines: string[] = [];
    for (const rawLine of text.split(/\r?\n/)) {
      if (rawLine.startsWith('data:')) {
        dataLines.push(rawLine.slice(5).trimStart());
        continue;
      }
      if (rawLine === '' && dataLines.length > 0) {
        try {
          const msg = JSON.parse(dataLines.join('\n')) as Record<string, unknown>;
          if (('result' in msg || 'error' in msg) && msg['id'] === expectedId) return msg;
        } catch { /* skip malformed frame */ }
        dataLines = [];
      }
    }
    return null;
  }
  const msg = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  // Per JSON-RPC, a response is only valid if it carries result/error AND
  // matches the request id. A mismatched/malformed message must not leak to the
  // caller (it would let a wrong-id error/result drive the verdict) — return null
  // and let the caller treat it as "no confirmable failure" (Phase 1 governs).
  if (msg && ('result' in msg || 'error' in msg) && msg['id'] === expectedId) return msg;
  return null;
}

export interface ComposioToolCallProbeConfig {
  /** The agent's wired MCP URL (`…/v3/mcp/<serverId>/mcp?user_id=…`). */
  url: string;
  /** Headers from the wired server (carries `x-api-key`). */
  headers?: Record<string, string>;
  timeoutMs?: number;
  /**
   * ENG-6212 — operator-stored override: call THIS specific tool (e.g.
   * `GMAIL_GET_PROFILE`) instead of auto-picking. Honoured only when the slug is
   * present in the live tools/list AND structurally read-only; otherwise the
   * probe silently falls back to the heuristic pick and reports the reason via
   * `outcome.details.override_fallback` (caller logs reason=seed-drift/-invalid).
   */
  toolName?: string | null;
  /** Args for the override tool (default `{}`). Ignored unless the override is used. */
  toolArgs?: Record<string, unknown> | null;
}

/**
 * Probe the wired Composio MCP server with a safe read-only tool call. See the
 * module header for the outcome contract. Never throws.
 */
export async function probeComposioMcpToolCall(
  config: ComposioToolCallProbeConfig,
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectivityProbeOutcome | null> {
  const timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const baseHeaders: Record<string, string> = {
    ...(config.headers ?? {}),
    'Content-Type': 'application/json',
    Accept: MCP_ACCEPT,
  };

  try {
    // 1. initialize
    const initRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'initialize',
        params: {
          protocolVersion: '2025-03-26',
          capabilities: {},
          clientInfo: { name: 'augmented-toolcall-probe', version: '1.0.0' },
        },
      }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!initRes.ok) {
      return initRes.status >= 500
        ? { status: 'transient_error', message: `MCP initialize returned ${initRes.status}` }
        : null; // 4xx on handshake → can't run Phase 2; let Phase 1 govern.
    }
    const sessionId = initRes.headers.get('mcp-session-id');
    await parseRpc(initRes, 1);
    const sessionHeaders = { ...baseHeaders, ...(sessionId ? { 'Mcp-Session-Id': sessionId } : {}) };

    // 2. notifications/initialized
    const initializedRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({ jsonrpc: '2.0', method: 'notifications/initialized' }),
      signal: AbortSignal.timeout(5_000),
    });
    await initializedRes.text().catch(() => '');

    // 3. tools/list — find a provably safe tool to call.
    const listRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({ jsonrpc: '2.0', id: 2, method: 'tools/list' }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!listRes.ok) {
      return listRes.status >= 500
        ? { status: 'transient_error', message: `MCP tools/list returned ${listRes.status}` }
        : null;
    }
    const listRpc = await parseRpc(listRes, 2);
    const tools = ((listRpc?.['result'] as { tools?: McpToolDescriptor[] } | undefined)?.tools) ?? [];
    // ENG-6212: honour an operator-stored override when it's present in the
    // live list AND structurally read-only; otherwise fall back to the
    // heuristic pick and record why. resolveProbeTool never returns a tool that
    // isn't provably read-only, so the invariant holds for stored values too.
    const resolved = resolveProbeTool(tools, { tool: config.toolName, args: config.toolArgs });
    const toolName = resolved.toolName;
    if (!toolName) return null; // no safe tool — skip, Phase 1 governs.
    // Carried into every outcome so the caller can log reason=seed-drift/-invalid
    // and the UI can show which tool actually ran.
    const baseDetails: Record<string, unknown> = {
      tool: toolName,
      ...(resolved.fallback ? { override_fallback: resolved.fallback, requested_tool: resolved.requestedTool } : {}),
    };

    // 4. tools/call — read-only tool, stored args only when the override is used.
    const callRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 3,
        method: 'tools/call',
        params: { name: toolName, arguments: resolved.args },
      }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!callRes.ok) {
      return callRes.status >= 500
        ? { status: 'transient_error', message: `MCP tools/call returned ${callRes.status}` }
        : null;
    }
    const callRpc = await parseRpc(callRes, 3);

    // ENG-6212: capture a truncated raw response for the Test modal's
    // collapsed "technical details" — the read-only tool's own output (a
    // profile blob, an error string). Surfaced on every outcome below.
    // ENG-6224: cap at 2000 (was 600) so a typical profile JSON stays a
    // COMPLETE, parseable object for the modal's pretty-print + summary.
    {
      const errText = (callRpc?.['error'] as { message?: string } | undefined)?.message;
      const resContent = (callRpc?.['result'] as { content?: Array<{ text?: string }> } | undefined)?.content;
      const raw = errText ?? (resContent ?? []).map((c) => c.text ?? '').join(' ').trim();
      if (raw) baseDetails.response = raw.length > 2000 ? `${raw.slice(0, 2000)}…` : raw;
    }

    // A failure can arrive in any of three shapes:
    //   1. a JSON-RPC protocol error,
    //   2. a tool-level error (`result.isError` + content), or
    //   3. (ENG-6328) a Composio `successful:false` envelope embedded in the
    //      result content — Composio wraps an UPSTREAM provider failure (e.g. a
    //      Linear 401) as a SUCCESSFUL MCP call whose payload says
    //      `successful:false`, with NO isError flag. Pre-fix this fell through to
    //      a green "resolved the account", so a revoked OAuth grant read OK.
    const rpcErrMsg =
      callRpc && 'error' in callRpc
        ? (callRpc['error'] as { message?: string } | undefined)?.message ?? ''
        : '';
    const result = callRpc?.['result'] as { isError?: boolean; content?: Array<{ text?: string }> } | undefined;
    const contentText = (result?.content ?? []).map((c) => c.text ?? '').join(' ').trim();
    const failed = Boolean(rpcErrMsg) || Boolean(result?.isError) || isComposioFailureEnvelope(contentText);

    if (failed) {
      const failureText = [rpcErrMsg, contentText].filter(Boolean).join(' ');
      const snippet = failureText.length > 200 ? `${failureText.slice(0, 200)}…` : failureText;
      const kind = classifyToolCallFailure(failureText);
      if (kind === 'account') {
        return {
          status: 'down',
          message: `Live tool call '${toolName}' failed to resolve the connected account: ${snippet}`,
          details: baseDetails,
        };
      }
      if (kind === 'quota') {
        // ENG-8772: the account resolved, the credential was ACCEPTED, and the
        // provider refused the call because a quota or rate limit is exhausted.
        // Nothing is broken and nothing needs reconnecting — but the integration
        // is unusable until the window resets, so this must never read as a
        // pass. `degraded` rather than `down` for the same reason as
        // scope_deficit: the credential is proven good, and writing status
        // 'error' would de-provision the agent's tools (the ENG-6042 landmine).
        const retryAfterSeconds = extractRetryAfterSeconds(failureText);
        const retryPhrase = retryAfterSeconds
          ? ` The provider says to retry in ${formatRetryWindow(retryAfterSeconds)}.`
          : '';
        return {
          status: 'degraded',
          message:
            `Live tool call '${toolName}' reached the provider and was refused: a quota or rate ` +
            `limit is exhausted. The connection itself is valid — do NOT reconnect, this resolves ` +
            `when the window resets.${retryPhrase} ${snippet}`,
          details: {
            ...baseDetails,
            reason: 'quota_exhausted',
            ...(retryAfterSeconds ? { retry_after_seconds: retryAfterSeconds } : {}),
          },
        };
      }
      if (kind === 'scope') {
        // ENG-8406: the account resolved AND the credential is VALID — the
        // provider refused this tool because the connection's app/token isn't
        // GRANTED the scope it needs (e.g. a Shopify custom app missing
        // `read_content`). This is NOT a reconnect: re-authorising the same app
        // changes nothing. The operator must add the required scope to the
        // provider app (and re-install/re-authorise). Marked `degraded`, not
        // `down`, so the UI doesn't show a misleading "reconnect required".
        return {
          status: 'degraded',
          message:
            `Live tool call '${toolName}' was refused for a missing permission — the connection is ` +
            `valid, but its app/token isn't granted the scope this tool needs. Grant the required ` +
            `scope in the provider app and re-authorise (this is NOT a reconnect): ${snippet}`,
          details: { ...baseDetails, reason: 'scope_deficit' },
        };
      }
      if (kind === 'auth') {
        // ENG-6328: the wired server RESOLVED the account, but the upstream
        // provider rejected its credential (401/403, "authentication required").
        // Composio still reports the account ACTIVE with
        // auth_refresh_required:false, so the account + auth_config checks pass —
        // only this live call proves the OAuth grant is dead. Reconnect required.
        return {
          status: 'down',
          message:
            `Live tool call '${toolName}' was rejected by the provider — the connection's ` +
            `credential is no longer valid (reconnect required): ${snippet}`,
          details: { ...baseDetails, reason: 'upstream_auth_rejected' },
        };
      }
      if (kind === 'site') {
        // ENG-7732: the account resolved but the derived provider site/base URL
        // is empty or malformed (the bare scheme became the hostname), so no
        // real host was reached - e.g. a Jira connection whose account has no
        // accessible Atlassian site. Reconnect and grant a site.
        return {
          status: 'down',
          message:
            `Live tool call '${toolName}' couldn't reach the provider's site - the connection ` +
            `has no valid site URL (reconnect and make sure a site/workspace is granted): ${snippet}`,
          details: { ...baseDetails, reason: 'site_unresolved' },
        };
      }
      // A benign tool error (bad args, validation) still proves the account
      // resolved AND the credential was accepted.
      //
      // ENG-8772: this `ok` is QUALIFIED — the call did not plainly succeed, it
      // failed in a way that happens to prove the credential. `tool_error` says
      // so structurally so a caller cannot render it as an unqualified
      // "call succeeded". The caller previously discarded this outcome's
      // `message` and appended exactly that phrase, which is how a failed call
      // reached the operator as a green VERIFIED. Flagging it in `details`
      // rather than making the caller sniff the message text keeps the two
      // sides from drifting.
      return {
        status: 'ok',
        message: `Live tool call '${toolName}' resolved the account (tool error: ${snippet})`,
        details: { ...baseDetails, tool_error: snippet },
      };
    }

    return { status: 'ok', message: `Live tool call '${toolName}' resolved the connected account`, details: baseDetails };
  } catch (err) {
    const isAbort = (err as Error)?.name === 'TimeoutError' || (err as Error)?.name === 'AbortError';
    return {
      status: 'transient_error',
      message: isAbort
        ? `MCP tool-call probe timed out after ${timeoutMs / 1000}s`
        : `MCP tool-call probe failed: ${(err as Error).message}`,
    };
  }
}
